import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { usePhotoUrl } from '@/hooks/usePhotos'
import { useVideo } from '@/hooks/useVideos'
import { proposalService, sharedPhotoUrl } from '@/services/ProposalService'
import { proposalKeys } from '@/hooks/queryKeys'
import { cn } from '@/lib/utils'
import { shrinkForPrint } from '@/proposal-document/printPhoto'

/**
 * Where the document's photographs come from.
 *
 * Storage is private, so a photo is never a plain path. A client reading a
 * shared link has no session, and gets a route authorized by their token that
 * only serves photos on their proposal. Staff use the ordinary signed link.
 * The wording preview renders a sample with no photos at all.
 */
export type PhotoSource =
  | {
      kind: 'shared'
      token: string
      /**
       * For the PDF printer: every photo loads up front, since the browser
       * printing it scrolls nothing, and is shrunk to print size before it
       * is printed (see `printPhoto.ts`).
       */
      forPrint: true
    }
  | { kind: 'shared'; token: string; forPrint: false }
  | { kind: 'staff' }
  | { kind: 'none' }

const PhotoSourceContext = React.createContext<PhotoSource>({ kind: 'none' })

export function PhotoSourceProvider({
  source,
  children,
}: {
  source: PhotoSource
  children: React.ReactNode
}) {
  return (
    <PhotoSourceContext.Provider value={source}>
      {children}
    </PhotoSourceContext.Provider>
  )
}

/** Whether this document can show photographs at all. */
export function useCanShowPhotos(): boolean {
  return React.useContext(PhotoSourceContext).kind !== 'none'
}

interface PhotoProps {
  documentId: string
  alt: string
  className?: string
  /** Load straight away: the cover, never a photo further down. */
  eager?: boolean
}

/**
 * One photograph from the survey.
 *
 * A photo that fails to load hides itself rather than leaving a broken-image
 * box on a client's copy: the finding beside it still says what was found.
 */
export function ProposalPhoto(props: PhotoProps) {
  const source = React.useContext(PhotoSourceContext)
  if (source.kind === 'shared') {
    return source.forPrint ? (
      <PrintPhoto {...props} token={source.token} />
    ) : (
      <SharedPhoto {...props} token={source.token} />
    )
  }
  if (source.kind === 'staff') return <StaffPhoto {...props} />
  return null
}

function SharedPhoto({
  documentId,
  alt,
  className,
  eager,
  token,
}: PhotoProps & { token: string }) {
  const [failed, setFailed] = React.useState(false)
  if (failed) return null
  return (
    <img
      src={sharedPhotoUrl(token, documentId)}
      alt={alt}
      loading={eager === true ? 'eager' : 'lazy'}
      decoding="async"
      className={cn('proposal-img', className)}
      onError={() => {
        setFailed(true)
      }}
    />
  )
}

/**
 * A photo as the PDF printer gets it: loaded at once, then swapped for a
 * print-sized copy. `data-print-pending` stays on it until that copy is in,
 * so the print page does not say it is ready too soon.
 *
 * Read with CORS, which the API allows the printer (the bytes come straight
 * from the API on a render grant). A photo that cannot be read prints as it
 * is, larger but whole.
 */
function PrintPhoto({ documentId, alt, className, token }: PhotoProps & { token: string }) {
  const [shrunk, setShrunk] = React.useState<string | null>(null)
  const [isDone, setDone] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(
    () => () => {
      if (shrunk !== null) URL.revokeObjectURL(shrunk)
    },
    [shrunk],
  )

  if (failed) return null
  return (
    <img
      src={shrunk ?? sharedPhotoUrl(token, documentId)}
      alt={alt}
      crossOrigin="anonymous"
      loading="eager"
      className={cn('proposal-img', className)}
      data-print-pending={isDone ? undefined : ''}
      onLoad={(event) => {
        if (shrunk !== null) {
          setDone(true)
          return
        }
        void shrinkForPrint(event.currentTarget).then((blob) => {
          if (blob === null) setDone(true)
          else setShrunk(URL.createObjectURL(blob))
        })
      }}
      onError={() => {
        setFailed(true)
      }}
    />
  )
}

function StaffPhoto({ documentId, alt, className, eager }: PhotoProps) {
  const url = usePhotoUrl(documentId)
  const [failed, setFailed] = React.useState(false)
  if (url.data === undefined || failed) {
    return url.isPending ? (
      <span className={cn('proposal-img proposal-img--pending', className)} />
    ) : null
  }
  return (
    <img
      src={url.data}
      alt={alt}
      loading={eager === true ? 'eager' : 'lazy'}
      decoding="async"
      className={cn('proposal-img', className)}
      onError={() => {
        setFailed(true)
      }}
    />
  )
}

/** "0:42" from seconds; null when Stream has not reported a length. */
function clipLength(seconds: number | null): string | null {
  if (seconds === null) return null
  const minutes = Math.floor(seconds / 60)
  return `${String(minutes)}:${String(seconds % 60).padStart(2, '0')}`
}

interface VideoProps {
  videoId: string
  durationSeconds: number | null
  /** What the clip is of, for the button and the player's title. */
  label: string
  onPlay?: () => void
}

/**
 * A clip the arborist recorded of this tree, played on request.
 *
 * Nothing loads until somebody presses play: Stream signs playback links and
 * they expire, and a page of trees should not mint a dozen links nobody uses.
 * Stream's own iframe player, as on the survey screen, because it handles
 * adaptive streaming and the signed token.
 */
export function ProposalVideo({ videoId, durationSeconds, label, onPlay }: VideoProps) {
  const source = React.useContext(PhotoSourceContext)
  const [playing, setPlaying] = React.useState(false)
  if (source.kind === 'none') return null

  if (!playing) {
    const length = clipLength(durationSeconds)
    return (
      <button
        type="button"
        className="proposal-video-start"
        onClick={() => {
          setPlaying(true)
          onPlay?.()
        }}
      >
        <span aria-hidden>▶</span>
        <span>
          Watch the arborist on {label}
          {length === null ? null : <span className="proposal-mono"> · {length}</span>}
        </span>
      </button>
    )
  }

  return source.kind === 'shared' ? (
    <SharedVideo videoId={videoId} label={label} token={source.token} />
  ) : (
    <StaffVideo videoId={videoId} label={label} />
  )
}

function VideoFrame({ url, label }: { url: string; label: string }) {
  return (
    <iframe
      src={url}
      title={`Video of ${label}`}
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
      allowFullScreen
      className="proposal-video"
    />
  )
}

function VideoState({ isError }: { isError: boolean }) {
  return (
    <p className="proposal-small">
      {isError ? 'That video could not be played.' : 'Loading the video…'}
    </p>
  )
}

function SharedVideo({ videoId, label, token }: { videoId: string; label: string; token: string }) {
  const playback = useQuery({
    // The link it returns expires, so it is never cached for long.
    queryKey: proposalKeys.sharedVideo(token, videoId),
    queryFn: () => proposalService.getSharedVideoPlayback(token, videoId),
    staleTime: 4 * 60_000,
    retry: false,
  })
  if (playback.data === undefined) return <VideoState isError={playback.isError} />
  return <VideoFrame url={playback.data.playbackUrl} label={label} />
}

function StaffVideo({ videoId, label }: { videoId: string; label: string }) {
  const video = useVideo(videoId)
  if (video.data?.playbackUrl == null) return <VideoState isError={video.isError} />
  return <VideoFrame url={video.data.playbackUrl} label={label} />
}
